import { defineStore } from 'pinia'
import type { RealtimeChannel } from '@supabase/supabase-js'
import type { ServiceCatalog, ClinicSharedService } from '~/types/payment'
import type { PaymentMethod } from '~/types/payments_module'
import { translateSupabaseError } from '~/utils/supabase-error'
import { assertAffected } from '~/utils/assertAffected'

// 'auto' = sigue prefers-color-scheme del sistema (mig 1166); se resuelve a 'dark' o al tema
// claro (custom de la clínica si existe, si no teal) en layouts/dashboard.vue antes de
// llegar a resolvedThemeVars / resolvedSidebarDark.
export type ThemePreset = 'auto' | 'teal' | 'rose' | 'forest' | 'dark' | 'warm' | 'custom'
export type ConcreteThemePreset = Exclude<ThemePreset, 'custom' | 'auto'>

/** Tema claro/oscuro concreto para un preset 'auto' según la preferencia del sistema. */
export function resolveAutoPreset(prefersDark: boolean, clinicHasCustomColors: boolean): ThemePreset {
  if (prefersDark) return 'dark'
  return clinicHasCustomColors ? 'custom' : 'teal'
}

type ThemeVarMap = {
  '--color-primary':      string
  '--color-primary-text': string
  '--color-accent':       string
  '--color-sidebar-bg':   string
  '--color-sidebar-text': string
  '--color-bg':           string
  '--color-surface':      string
  '--color-border':       string
  '--color-text':         string
  '--color-text-muted':   string
}

export const THEME_PRESETS: Record<ConcreteThemePreset, ThemeVarMap & { sidebarDark: boolean }> = {
  teal: {
    '--color-primary':      '#0F766E',
    '--color-primary-text': '#ffffff',
    '--color-accent':       '#F0FDFA',
    '--color-sidebar-bg':   '#ffffff',
    '--color-sidebar-text': '#374151',
    '--color-bg':           '#F9FAFB',
    '--color-surface':      '#ffffff',
    '--color-border':       '#E5E7EB',
    '--color-text':         '#111827',
    '--color-text-muted':   '#5F6775', // ≥4.5:1 también sobre chips bg-gray-100 (G-UX-013); antes #6B7280 = 4.2:1
    sidebarDark: false,
  },
  rose: {
    '--color-primary':      '#BE185D',
    '--color-primary-text': '#ffffff',
    '--color-accent':       '#FCE7F3',
    '--color-sidebar-bg':   '#FFF1F5',
    '--color-sidebar-text': '#881337',
    '--color-bg':           '#FFFAFB',
    '--color-surface':      '#ffffff',
    '--color-border':       '#FBCFE8',
    '--color-text':         '#111827',
    '--color-text-muted':   '#655E66', // G-UX-013 (antes #78717A)
    sidebarDark: false,
  },
  forest: {
    '--color-primary':      '#16A34A',
    '--color-primary-text': '#ffffff',
    '--color-accent':       '#DCFCE7',
    '--color-sidebar-bg':   '#14532D',
    '--color-sidebar-text': '#D1FAE5',
    '--color-bg':           '#F0FDF4',
    '--color-surface':      '#ffffff',
    '--color-border':       '#BBF7D0',
    '--color-text':         '#14532D',
    '--color-text-muted':   '#566359', // G-UX-013 (antes #6B7870)
    sidebarDark: true,
  },
  dark: {
    '--color-primary':      '#6366F1',
    '--color-primary-text': '#ffffff',
    '--color-accent':       '#312E81',
    '--color-sidebar-bg':   '#111827',
    '--color-sidebar-text': '#E5E7EB',
    '--color-bg':           '#1F2937',
    '--color-surface':      '#374151',
    '--color-border':       '#4B5563',
    '--color-text':         '#F9FAFB',
    '--color-text-muted':   '#B4BAC4', // G-UX-013: sobre surface #374151 #9CA3AF era 3.96:1
    sidebarDark: true,
  },
  warm: {
    '--color-primary':      '#B45309',
    '--color-primary-text': '#ffffff',
    '--color-accent':       '#FEF3C7',
    '--color-sidebar-bg':   '#FAFAF9',
    '--color-sidebar-text': '#44403C',
    '--color-bg':           '#FAFAF9',
    '--color-surface':      '#ffffff',
    '--color-border':       '#E7E5E4',
    '--color-text':         '#1C1917',
    '--color-text-muted':   '#68615C', // G-UX-013 (antes #78716C)
    sidebarDark: false,
  },
}

export function resolvedThemeVars(branding: ClinicBranding): ThemeVarMap {
  if (branding.theme_preset !== 'custom') {
    // 'auto' llega ya resuelto desde el layout; si no, cae a teal (nunca a undefined).
    const key = (branding.theme_preset === 'auto' ? 'teal' : branding.theme_preset) as ConcreteThemePreset
    const { sidebarDark: _, ...vars } = THEME_PRESETS[key] ?? THEME_PRESETS.teal
    return vars
  }
  const { sidebarDark: _, ...tealDefaults } = THEME_PRESETS.teal
  return {
    ...tealDefaults,
    '--color-primary':      branding.primary_color,
    '--color-primary-text': luminance(branding.primary_color) > 0.5 ? '#111827' : '#ffffff',
    '--color-accent':       branding.accent_color,
    '--color-sidebar-bg':   branding.secondary_color,
  }
}

// WCAG relative luminance (IEC 61966-2-1 gamma). The old simplified formula
// (0.299R + 0.587G + 0.114B) / 255 overstates luminance for saturated mid-range
// colors — e.g. a medium purple like #9B72CB scores 0.53 (→ dark text) but
// visually looks dark. WCAG gamma correction gives 0.26 (→ white text, correct).
function luminance(hex: string): number {
  const h = hex.replace('#', '')
  if (h.length !== 6) return 1
  const toLinear = (c: number) => {
    const s = c / 255
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4
  }
  const r = toLinear(parseInt(h.slice(0, 2), 16))
  const g = toLinear(parseInt(h.slice(2, 4), 16))
  const b = toLinear(parseInt(h.slice(4, 6), 16))
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function resolvedSidebarDark(branding: ClinicBranding): boolean {
  if (branding.theme_preset !== 'custom') {
    const key = (branding.theme_preset === 'auto' ? 'teal' : branding.theme_preset) as ConcreteThemePreset
    return (THEME_PRESETS[key] ?? THEME_PRESETS.teal).sidebarDark
  }
  return luminance(branding.secondary_color) < 0.5
}

export interface ClinicBranding {
  logo_url:        string | null
  theme_preset:    ThemePreset
  primary_color:   string
  secondary_color: string
  accent_color:    string
}

export const DEFAULT_BRANDING: ClinicBranding = {
  logo_url:        null,
  theme_preset:    'teal',
  primary_color:   '#0F766E',
  secondary_color: '#ffffff',
  accent_color:    '#F0FDFA',
}

export interface Clinic {
  id:             string
  name:           string
  slug:           string
  address:        string | null
  phone:          string | null
  email:          string | null
  timezone:       string
  is_active:      boolean
  branding:       ClinicBranding
  clues:          string | null
  suspended_at:   string | null
  suspend_reason: string | null
  /** Auto-enviar el enlace de antecedentes por WhatsApp al registrar paciente nueva (M-PAT-076). */
  autoSendEhrOnRegister:       boolean
  default_phone_country_code:  string   // snake_case to match DB column
  /** Coordenadas que anclan el mapa del marketplace de laboratorios (mig 983). */
  lat:                         number | null
  lng:                         number | null
}

function parseClinic(raw: any): Clinic {
  const b = raw.settings?.branding
  return {
    id:             raw.id,
    name:           raw.name,
    slug:           raw.slug,
    address:        raw.address,
    phone:          raw.phone,
    email:          raw.email,
    timezone:       raw.timezone,
    is_active:      raw.is_active,
    clues:          raw.clues ?? null,
    suspended_at:   raw.suspended_at   ?? null,
    suspend_reason: raw.suspend_reason ?? null,
    autoSendEhrOnRegister:      raw.auto_send_ehr_on_register   ?? true,
    default_phone_country_code: raw.default_phone_country_code ?? '52',
    lat: raw.lat ?? null,
    lng: raw.lng ?? null,
    branding: {
      logo_url:        b?.logo_url        ?? null,
      theme_preset:    b?.theme_preset    ?? 'teal',
      primary_color:   b?.primary_color   ?? DEFAULT_BRANDING.primary_color,
      secondary_color: b?.secondary_color ?? DEFAULT_BRANDING.secondary_color,
      accent_color:    b?.accent_color    ?? DEFAULT_BRANDING.accent_color,
    },
  }
}

const CLINIC_SELECT = 'id, name, slug, address, phone, email, timezone, is_active, settings, clues, suspended_at, suspend_reason, auto_send_ehr_on_register, default_phone_country_code, lat, lng'

const STALE_MS = 30 * 60_000  // 30 min

// ── Agenda pública (booking_settings, Task 11 — self-booking v1) ───────────
// Una fila por clínica (PK clinic_id). RLS: solo admin de la propia clínica
// lee/escribe (migration 1200_public_booking_tables.sql).
export interface BookingSettings {
  clinic_id:             string
  enabled:               boolean
  auto_confirm:          boolean
  lead_time_min:         number
  max_days_ahead:        number
  consultation_type_ids: string[]
  policy_text:           string | null
  public_host:           string | null
  public_site_origin:    string | null
  updated_at:            string
}

function defaultBookingSettings(clinicId: string): BookingSettings {
  return {
    clinic_id: clinicId, enabled: false, auto_confirm: false,
    lead_time_min: 120, max_days_ahead: 30, consultation_type_ids: [],
    policy_text: null, public_host: null, public_site_origin: null,
    updated_at: new Date().toISOString(),
  }
}

interface SettingsState {
  clinic:                  Clinic | null
  catalog:                 ServiceCatalog[]
  clinicCatalog:           ClinicSharedService[]
  paymentMethods:          PaymentMethod[]
  bookingSettings:         BookingSettings | null
  loading:                 boolean
  saving:                  boolean
  error:                   string | null
  lastFetchClinic:         number | null
  lastFetchCatalog:        number | null
  lastFetchClinicCatalog:  number | null
  lastFetchPaymentMethods: number | null
  clinicChannel:           RealtimeChannel | null
}

export const useSettingsStore = defineStore('settings', {
  state: (): SettingsState => ({
    clinic: null, catalog: [], clinicCatalog: [], paymentMethods: [],
    bookingSettings: null,
    loading: false, saving: false, error: null,
    lastFetchClinic: null, lastFetchCatalog: null, lastFetchClinicCatalog: null,
    lastFetchPaymentMethods: null,
    clinicChannel: null,
  }),

  actions: {
    // ── Clínica ──────────────────────────────────────────────
    async fetchClinic(force = false) {
      if (!force && this.lastFetchClinic && Date.now() - this.lastFetchClinic < STALE_MS && this.clinic !== null) return
      const supabase = useSupabaseClient()
      this.loading   = true
      this.error     = null
      try {
        // Use auth_clinic_id() RPC as source of truth — honors impersonation
        // and works with super-admin's permissive RLS (mig 330) that returns
        // all clinics. Without explicit .eq() filter, .single() would fail.
        const { data: clinicId, error: idErr } = await supabase.rpc('auth_clinic_id')
        if (idErr) throw idErr
        const { data, error } = await supabase
          .from('clinics')
          .select(CLINIC_SELECT)
          .eq('id', clinicId)
          .single()
        if (error) throw error
        this.clinic = parseClinic(data)
        this.lastFetchClinic = Date.now()
      } catch (e: any) {
        this.error = translateSupabaseError(e)
      } finally { this.loading = false }
    },

    async updateClinic(payload: Partial<Omit<Clinic, 'id' | 'slug' | 'is_active' | 'branding'>>) {
      const supabase = useSupabaseClient()
      this.saving    = true
      this.error     = null
      try {
        const { data, error } = await supabase
          .from('clinics')
          .update(payload)
          .eq('id', this.clinic!.id)
          .select(CLINIC_SELECT)
          .single()
        if (error) throw error
        this.clinic = parseClinic(data)
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally { this.saving = false }
    },

    // ── Auto-envío antecedentes (M-PAT-076) ──────────────────
    async updateAutoSendEhr(enabled: boolean) {
      const supabase = useSupabaseClient()
      this.saving    = true
      this.error     = null
      try {
        const { data, error } = await supabase
          .from('clinics')
          .update({ auto_send_ehr_on_register: enabled })
          .eq('id', this.clinic!.id)
          .select(CLINIC_SELECT)
          .single()
        if (error) throw error
        this.clinic = parseClinic(data)
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally { this.saving = false }
    },

    // ── Branding ─────────────────────────────────────────────
    async updateBranding(branding: Partial<ClinicBranding>) {
      const supabase = useSupabaseClient()
      this.saving    = true
      this.error     = null
      try {
        const current    = this.clinic?.branding ?? DEFAULT_BRANDING
        const newBranding = { ...current, ...branding }
        const { data, error } = await supabase
          .from('clinics')
          .update({ settings: { branding: newBranding } })
          .eq('id', this.clinic!.id)
          .select(CLINIC_SELECT)
          .single()
        if (error) throw error
        this.clinic = parseClinic(data)
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally { this.saving = false }
    },

    async uploadLogo(file: File) {
      const supabase = useSupabaseClient()
      this.saving    = true
      this.error     = null
      try {
        // Logo en R2 (bucket público assets) vía presign — Supabase Storage
        // retirado 2026-07. El server construye el path clinic-logos/<clinic>/…
        // (M-DI-048). admin-upload-clinic-logo (flujo super-admin) queda aparte.
        const { data: up, error: upErr } = await supabase.functions.invoke('r2-upload', {
          body: { bucket: 'assets', asset_kind: 'clinic-logo', file_name: file.name, file_type: file.type, file_size: file.size },
        })
        if (upErr || up?.error || !up?.presigned_url) {
          throw new Error(up?.error ?? upErr?.message ?? 'No se pudo subir el logo')
        }
        const put = await fetch(up.presigned_url, {
          method: 'PUT', body: file, headers: { 'Content-Type': file.type },
        })
        if (!put.ok) throw new Error(`Upload falló: ${put.status}`)
        await this.updateBranding({ logo_url: up.public_url })
        return up.public_url
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally { this.saving = false }
    },

    // ── Catálogo de servicios ────────────────────────────────
    async fetchCatalog(force = false) {
      if (!force && this.lastFetchCatalog && Date.now() - this.lastFetchCatalog < STALE_MS && this.catalog.length > 0) return
      const supabase = useSupabaseClient()
      this.loading   = true
      this.error     = null
      try {
        const { data, error } = await supabase
          .from('service_catalog')
          .select('*')
          .is('deleted_at', null)
          .order('name')
        if (error) throw error
        this.catalog = data as ServiceCatalog[]
        this.lastFetchCatalog = Date.now()
      } catch (e: any) {
        this.error = translateSupabaseError(e)
      } finally { this.loading = false }
    },

    async createService(payload: { name: string; description?: string; price: number; tax_rate: number }) {
      const supabase = useSupabaseClient()
      this.saving    = true
      this.error     = null
      try {
        const { data: userData, error: userErr } = await supabase.auth.getUser()
        if (userErr || !userData.user) throw userErr ?? new Error('Usuario no autenticado')
        const { data, error } = await supabase
          .from('service_catalog')
          .insert({ ...payload, clinic_id: this.clinic!.id, doctor_id: userData.user.id })
          .select()
          .single()
        if (error) throw error
        this.catalog.push(data as ServiceCatalog)
        this.catalog.sort((a, b) => a.name.localeCompare(b.name))
        return data as ServiceCatalog
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally { this.saving = false }
    },

    async updateService(id: string, payload: Partial<ServiceCatalog>) {
      const supabase = useSupabaseClient()
      this.saving    = true
      this.error     = null
      try {
        const { error } = await supabase
          .from('service_catalog')
          .update(payload)
          .eq('id', id)
        if (error) throw error
        const idx = this.catalog.findIndex(s => s.id === id)
        if (idx !== -1) this.catalog[idx] = { ...this.catalog[idx], ...payload }
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally { this.saving = false }
    },

    async deleteService(id: string) {
      const supabase = useSupabaseClient()
      this.saving    = true
      this.error     = null
      try {
        const { data, error } = await supabase
          .from('service_catalog')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', id)
          .select('id')
        if (error) throw error
        assertAffected(data, 'No se pudo eliminar el servicio. Verifica tus permisos o que siga existiendo.')
        this.catalog = this.catalog.filter(s => s.id !== id)
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally { this.saving = false }
    },

    async setDefaultConsultation(id: string | null) {
      const supabase  = useSupabaseClient()
      this.saving     = true
      this.error      = null
      try {
        // 1. Clear existing default for this clinic
        const { error: e1 } = await supabase
          .from('service_catalog')
          .update({ is_default_consultation: false })
          .eq('clinic_id', this.clinic!.id)
          .eq('is_default_consultation', true)
        if (e1) throw e1

        // 2. Set new default (if not clearing)
        if (id) {
          const { error: e2 } = await supabase
            .from('service_catalog')
            .update({ is_default_consultation: true })
            .eq('id', id)
          if (e2) throw e2
        }

        this.catalog = this.catalog.map(s => ({
          ...s,
          is_default_consultation: s.id === id,
        }))
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally { this.saving = false }
    },

    // ── Servicios compartidos por otros doctores de la clínica ──
    async fetchClinicCatalog(force = false) {
      if (!force && this.lastFetchClinicCatalog && Date.now() - this.lastFetchClinicCatalog < STALE_MS && this.clinicCatalog.length > 0) return
      const supabase = useSupabaseClient()
      this.loading   = true
      this.error     = null
      try {
        const { data, error } = await supabase.rpc('list_clinic_shared_services')
        if (error) throw error
        this.clinicCatalog = (data ?? []) as ClinicSharedService[]
        this.lastFetchClinicCatalog = Date.now()
      } catch (e: any) {
        this.error = translateSupabaseError(e)
      } finally { this.loading = false }
    },

    // ── Compartir / dejar de compartir un servicio propio ──────
    async toggleSharedService(id: string) {
      const supabase = useSupabaseClient()
      const svc = this.catalog.find(s => s.id === id)
      if (!svc) return
      const newVal = !svc.is_shared
      this.saving = true
      this.error  = null
      try {
        const { error } = await supabase
          .from('service_catalog')
          .update({ is_shared: newVal })
          .eq('id', id)
        if (error) throw error
        const idx = this.catalog.findIndex(s => s.id === id)
        if (idx !== -1) this.catalog[idx] = { ...this.catalog[idx], is_shared: newVal }
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally { this.saving = false }
    },

    // ── Copiar servicio compartido de otro doctor (RPC atómica) ──
    async copyService(serviceId: string) {
      const supabase = useSupabaseClient()
      this.saving    = true
      this.error     = null
      try {
        const { data, error } = await supabase
          .rpc('copy_service_catalog', { p_service_id: serviceId })
        if (error) throw error
        await this.fetchCatalog(true)
        return data as ServiceCatalog
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally { this.saving = false }
    },

    // ── Métodos de pago ──────────────────────────────────────
    async fetchPaymentMethods(force = false) {
      if (!force && this.lastFetchPaymentMethods && Date.now() - this.lastFetchPaymentMethods < STALE_MS && this.paymentMethods.length > 0) return
      const supabase = useSupabaseClient()
      this.loading   = true
      this.error     = null
      try {
        const { data, error } = await supabase
          .from('payment_methods')
          .select('*')
          .order('sort_order')
          .order('name')
        if (error) throw error
        this.paymentMethods = data as PaymentMethod[]
        this.lastFetchPaymentMethods = Date.now()
      } catch (e: any) {
        this.error = translateSupabaseError(e)
      } finally { this.loading = false }
    },

    async createPaymentMethod(name: string) {
      const supabase = useSupabaseClient()
      this.saving    = true
      this.error     = null
      try {
        const { data, error } = await supabase
          .from('payment_methods')
          .insert({ name, clinic_id: this.clinic!.id })
          .select()
          .single()
        if (error) throw error
        this.paymentMethods.push(data as PaymentMethod)
        return data as PaymentMethod
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally { this.saving = false }
    },

    async updatePaymentMethod(id: string, payload: Partial<PaymentMethod>) {
      const supabase = useSupabaseClient()
      this.saving    = true
      this.error     = null
      try {
        const { data, error } = await supabase
          .from('payment_methods')
          .update(payload)
          .eq('id', id)
          .select()
          .single()
        if (error) throw error
        const idx = this.paymentMethods.findIndex(m => m.id === id)
        if (idx !== -1) this.paymentMethods[idx] = data as PaymentMethod
        return data as PaymentMethod
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally { this.saving = false }
    },

    // ── Agenda pública (booking_settings) ────────────────────
    // Clinic id = settingsStore.clinic.id (final review M9): fetchClinic already resolved it
    // (impersonation included) — no extra auth_clinic_id() round trip per call.
    async bookingClinicId(): Promise<string> {
      if (!this.clinic?.id) await this.fetchClinic()
      const id = this.clinic?.id
      if (!id) throw new Error(this.error || 'No se pudo identificar la clínica')
      return id
    },

    async fetchBookingSettings() {
      const supabase = useSupabaseClient()
      this.bookingSettings = null
      this.loading = true
      this.error  = null
      try {
        const clinicId = await this.bookingClinicId()
        this.loading = true   // fetchClinic (if it had to run) resets it in its finally
        const { data, error } = await supabase
          .from('booking_settings')
          .select('*')
          .eq('clinic_id', clinicId)
          .maybeSingle()
        if (error) throw error
        this.bookingSettings = (data as BookingSettings) ?? defaultBookingSettings(clinicId)
      } catch (e: any) {
        this.error = translateSupabaseError(e)
      } finally { this.loading = false }
    },

    async saveBookingSettings(payload: Partial<Omit<BookingSettings, 'clinic_id' | 'updated_at'>>) {
      const supabase = useSupabaseClient()
      this.saving = true
      this.error  = null
      try {
        const clinicId = await this.bookingClinicId()
        const row = { clinic_id: clinicId, ...payload, updated_at: new Date().toISOString() }
        const { data, error } = await supabase
          .from('booking_settings')
          .upsert(row, { onConflict: 'clinic_id' })
          .select('*')
          .single()
        if (error) throw error
        this.bookingSettings = data as BookingSettings
        return this.bookingSettings
      } catch (e: any) {
        this.error = translateSupabaseError(e)
        throw e
      } finally { this.saving = false }
    },

    // ── Realtime — clinics ───────────────────────────────────
    subscribe(clinicId: string) {
      const supabase = useSupabaseClient()
      if (!clinicId || this.clinicChannel) return

      this.clinicChannel = supabase
        .channel(`settings:clinics:${clinicId}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'clinics', filter: `id=eq.${clinicId}` },
          () => { this.fetchClinic(true) },
        )
        .subscribe((status: string) => {
          if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
            setTimeout(() => { this.unsubscribe(); this.subscribe(clinicId) }, 5000)
          }
        })
    },

    unsubscribe() {
      if (!this.clinicChannel) return
      const supabase = useSupabaseClient()
      try { supabase.removeChannel(this.clinicChannel) } catch { /* noop */ }
      this.clinicChannel = null
    },
  },
})
